<template>
  <!-- Rendered inline by OrderModalHomeFooter in place of the normal
       Checkout/Actions row while a move-flow is running.  Same layout
       as that row — primary flex-1 Submit + small icon Cancel — so
       heights and paddings stay identical when the manager toggles
       select mode. -->
  <AppStack class="gap-2">
    <AppButton
      :disabled="!submitEnabled"
      :text="submitLabel"
      variant="secondary-blue"
      class="flex-1"
      @click="onSubmit"
    />
    <AppButton variant="outline-secondary" class="!px-3" @click="onCancel">
      <AppIcon
        color="text-fg-elevation-3-secondary"
        icon="cancel"
        size="custom"
        stroke-width="2px"
        class="w-[22px] h-[22px] relative"
      />
    </AppButton>
  </AppStack>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { toastController } from '@ionic/vue'
import { useI18n } from 'vue-i18n'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import AppButton from '~/components/AppButton.vue'
import { useInjectMoveItemsState } from '@restify/packages/composables/useMoveItemsState'
import useStores from '~/composables/useStores'

const { t } = useI18n()
const { orders: OrdersStore, 'storefront-places': StorefrontPlacesStore } =
  useStores()
const {
  moveItemsSourceOrderId,
  moveItemsTargetOrderId,
  moveItemsSelectedIds,
  clear: moveItemsClear,
} = useInjectMoveItemsState()

const submitLock = ref(false)

const sourceOrder = computed(() =>
  moveItemsSourceOrderId.value
    ? OrdersStore.getFromStore(moveItemsSourceOrderId.value).value
    : null,
)
const targetOrder = computed(() =>
  moveItemsTargetOrderId.value
    ? OrdersStore.getFromStore(moveItemsTargetOrderId.value).value
    : null,
)

const placeLabel = (order: NonNullable<typeof sourceOrder.value> | null) => {
  if (!order) return ''
  const placeId = order.storefrontPlaceIds?.[0]
  if (!placeId) return `#${order.publicId ?? ''}`
  const place = StorefrontPlacesStore.getFromStore(placeId).value
  const base =
    place && 'name' in place.config
      ? `#${place.config.name}`
      : `#${order.publicId ?? ''}`

  // Multi-order tables: append 1-based order index on the same place
  // (sorted by createdAt) so 3 orders on table #3 render as #3·1,
  // #3·2, #3·3 instead of all collapsing to #3.
  const ordersOnPlace = OrdersStore.findInStore({
    storefrontPlaceIds: placeId,
    'status.name': { $in: ['created', 'checking-out'] },
    $sort: { createdAt: 1 },
  })
    .map((r) => r.value)
    .filter((o): o is NonNullable<typeof o> => !!o)

  if (ordersOnPlace.length <= 1) return base

  const idx = ordersOnPlace.findIndex((o) => o._id === order._id)

  return idx === -1 ? base : `${base}·${idx + 1}`
}

// Rules:
//   * target must be set and different from source
//   * target must be in `created` status
const submitEnabled = computed(() => {
  if (submitLock.value) return false
  if (!targetOrder.value || !sourceOrder.value) return false
  if (targetOrder.value._id === sourceOrder.value._id) return false
  if (targetOrder.value.status?.name !== 'created') return false

  return moveItemsSelectedIds.value.length > 0
})

const submitLabel = computed(() => {
  if (!targetOrder.value) return t('order.moveItems.selectTarget')
  const from = placeLabel(sourceOrder.value ?? null)
  const to = placeLabel(targetOrder.value ?? null)
  if (from && to) return t('order.moveItems.submitTo', { from, to })
  return t('order.moveItems.submit')
})

const showToast = async (message: string, color: 'success' | 'danger') => {
  const toast = await toastController.create({
    message,
    duration: color === 'danger' ? 6000 : 2500,
    color,
    position: 'top',
  })
  await toast.present()
}

const onCancel = () => moveItemsClear()

const onSubmit = async () => {
  if (!submitEnabled.value || !sourceOrder.value || !targetOrder.value) return

  const source = sourceOrder.value
  const target = targetOrder.value
  const itemIds = [...moveItemsSelectedIds.value]

  // Snapshot everything we'll need for revert BEFORE mutating stores.
  const itemsToMove = (source.menuItems ?? []).filter((m) =>
    itemIds.includes(m._id),
  )
  const batchIdsInvolved = Array.from(
    new Set(itemsToMove.map((m) => m.batchId).filter(Boolean) as string[]),
  )
  // A batch is "fully moved" when every source-side item in it is
  // being transferred. In that case the batch entry migrates too;
  // otherwise we duplicate it into target with the original _id and
  // timestamps so `batchId` on the moved items still resolves.
  const fullyMovedBatchIds = batchIdsInvolved.filter((bid) => {
    const total = (source.menuItems ?? []).filter(
      (m) => m.batchId === bid,
    ).length
    const moving = itemsToMove.filter((m) => m.batchId === bid).length
    return moving === total
  })
  const batchesToAdd = (source.batches ?? []).filter((b) =>
    batchIdsInvolved.includes(b.id),
  )
  const addedBatchIds = batchesToAdd.map((b) => b.id)

  submitLock.value = true

  // Phase 1 — add to target
  try {
    await OrdersStore.patch(target._id, {
      $push: {
        menuItems: { $each: itemsToMove },
        ...(batchesToAdd.length && { batches: { $each: batchesToAdd } }),
      },
    } as unknown as Parameters<typeof OrdersStore.patch>[1])
  } catch (e) {
    submitLock.value = false
    await showToast(t('order.moveItems.errorNotApplied'), 'danger')
    return
  }

  // Phase 2 — remove from source
  try {
    await OrdersStore.patch(source._id, {
      $pull: {
        menuItems: { _id: { $in: itemIds } },
        ...(fullyMovedBatchIds.length && {
          batches: { id: { $in: fullyMovedBatchIds } },
        }),
      },
    } as unknown as Parameters<typeof OrdersStore.patch>[1])
  } catch (e) {
    // Revert phase 1: pull what we just pushed onto target.
    try {
      await OrdersStore.patch(target._id, {
        $pull: {
          menuItems: { _id: { $in: itemIds } },
          ...(addedBatchIds.length && {
            batches: { id: { $in: addedBatchIds } },
          }),
        },
      } as unknown as Parameters<typeof OrdersStore.patch>[1])
      submitLock.value = false
      await showToast(t('order.moveItems.errorReverted'), 'danger')
    } catch {
      submitLock.value = false
      await showToast(
        t('order.moveItems.errorBroken', { ids: itemIds.join(', ') }),
        'danger',
      )
    }
    return
  }

  submitLock.value = false
  moveItemsClear()
}
</script>
